/**
 * Os dois tetos mensais, que são o freio de gasto.
 *
 * Já moraram no rodapé da aba Análise, junto dos números. Ganharam lugar próprio porque as duas coisas
 * têm naturezas diferentes: Análise é leitura, para entender o fluxo; aqui se **decide** algo que faz o
 * widget parar de aparecer no site do cliente. Controle com essa consequência escondido embaixo de uma
 * tabela é fácil de não achar e fácil de mexer sem querer.
 *
 * ## Por que são dois tetos, e não um
 *
 * **Conversa** é fluxo iniciado. **Requisição** é carregamento de página — a config é buscada antes de
 * o botão flutuante existir, porque ele precisa da cor e da imagem.
 *
 * O de conversas nasceu primeiro e não é um freio de gasto: quem paga a conta é a requisição, e um pico
 * de page views sem conversa nenhuma passa inteiro por ele. Explicar essa diferença é metade do
 * trabalho desta tela — sem isso, quem escolhe um teto de conversas acredita ter limitado o gasto.
 *
 * ## Esta tela mede e avisa. Ela não bloqueia mais — e isso foi uma correção de segurança
 *
 * Os dois números já foram **portões**: atingidos, o servidor recusava a config e o widget desaparecia do
 * site do cliente. Saíram, e a razão está no cabeçalho de `core/getConfig.ts`: `Origin` é forjável fora de
 * um navegador, então **contagem forjável alimentando bloqueio é negação de serviço direcionada**. Quem
 * soubesse a chave pública — que está no HTML da página do cliente — desligava o widget dele. Medido: ~14 h
 * pelo teto de requisições, e ~3 minutos pelo de conversas, que ainda vinha ligado por padrão.
 *
 * Não havia conserto que mantivesse o portão: não existe como distinguir `Origin` forjado de legítimo.
 *
 * O que freia gasto hoje: rate limit por IP (aqui e na borda), regra de taxa na Cloudflare, orçamento com
 * alerta no faturamento, e `status: suspended` como parada dura — campo server-only, que o dono não
 * escreve, e que não deriva de contagem nenhuma.
 *
 * O consumo continua vindo do **mesmo contador** que o servidor escreve, não da soma dos dias de analytics
 * — somar aqui daria uma segunda conta, que divergiria na virada do dia. O que a tela precisa dizer, e diz,
 * é que o número inclui tráfego forjado: ruído em métrica é aceitável, ruído em portão não era.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_CONVERSATION_LIMIT,
  monthKey,
  isSuspended,
  SUSPENDED_REFUSAL_CACHE_SECONDS,
  resolveConversationLimit,
  resolveRequestLimit,
} from '@merlin/flow-engine';
import { useEditor } from '../../store/editor.js';
import { getDataSource, isDemoMode, type MonthlyUsage } from '../../data/index.js';
import { Badge, Field, IconButton, Input, Notice, ProgressBar, Section, Stat, Switch } from '../ui.js';
import { IconeAtualizar, IconeLimite } from '../icons.js';
import { SeloPropagacao } from './Propagacao.js';

/** Estado de um teto contra o consumo dele, para as duas metades da tela não repetirem a conta. */
function avaliar(usadas: number | null, teto: number | null) {
  const ilimitado = teto === null;
  const proporcao = teto && teto > 0 && usadas !== null ? Math.min(1, usadas / teto) : 0;
  const estourou = !ilimitado && usadas !== null && usadas >= teto;
  return { ilimitado, proporcao, estourou, perto: !estourou && proporcao >= 0.8 };
}

export function LimitSection() {
  const id = useEditor((state) => state.id);
  const conversationLimit = useEditor((state) => state.conversationLimit);
  const setConversationLimit = useEditor((state) => state.setConversationLimit);
  const requestLimit = useEditor((state) => state.requestLimit);
  const suspenso = isSuspended(useEditor((state) => state.status));
  const setRequestLimit = useEditor((state) => state.setRequestLimit);

  const [usadas, setUsadas] = useState<MonthlyUsage | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const mes = monthKey(Date.now());
  const tetoConversas = resolveConversationLimit(conversationLimit);
  const tetoRequisicoes = resolveRequestLimit(requestLimit);

  const carregar = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      setUsadas(await getDataSource().getMonthlyUsage(id, mes));
    } catch (caught) {
      setErro(caught instanceof Error ? caught.message : String(caught));
    }
  }, [id, mes]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const conversas = avaliar(usadas?.conversations ?? null, tetoConversas);
  const requisicoes = avaliar(usadas?.requests ?? null, tetoRequisicoes);
  const algumEstourou = conversas.estourou || requisicoes.estourou;

  const numero = (valor: number | undefined) =>
    valor === undefined ? '—' : valor.toLocaleString('pt-BR');

  return (
    <div className="space-y-6">
      {/*
        Suspensão é estado administrativo, não configuração: o campo é fechado nas Rules e o dono não
        edita. O aviso existe para ele entender por que o widget parou, em vez de procurar defeito no
        fluxo ou nos tetos abaixo — que continuam intactos e não têm nada a ver com isso.
      */}
      {suspenso ? (
        <Notice tone="danger" title="Este webchat está suspenso">
          O servidor está recusando a configuração, então o widget não aparece em nenhum site — e isso
          não tem relação com os tetos abaixo. É uma decisão administrativa da conta: fale com o
          responsável para reativar. Depois da reativação, o widget leva até{' '}
          {SUSPENDED_REFUSAL_CACHE_SECONDS / 60} minutos para voltar, porque a recusa fica guardada no
          CDN nesse período.
        </Notice>
      ) : null}

      <Section
        icon={IconeLimite}
        title={`Consumo de ${mes}`}
        description="Do contador que o servidor escreve. Mede — não bloqueia."
        action={
          <>
            {algumEstourou ? <Badge tone="danger">limite atingido</Badge> : null}
            {!algumEstourou && (conversas.perto || requisicoes.perto) ? (
              <Badge tone="warn">perto do limite</Badge>
            ) : null}
            <IconButton icon={IconeAtualizar} label="Atualizar consumo" onClick={() => void carregar()} />
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Stat
            label="Requisições"
            value={numero(usadas?.requests)}
            hint="Carregamentos de página que buscaram a configuração. É o que gera custo."
          />
          <Stat
            label="Conversas"
            value={numero(usadas?.conversations)}
            hint="Fluxos iniciados — o visitante abriu o widget e a primeira mensagem rodou."
          />
        </div>

        {!requisicoes.ilimitado && usadas ? (
          <ProgressBar
            value={requisicoes.proporcao}
            label={`Requisições em ${mes}: ${usadas.requests} de ${tetoRequisicoes}`}
            tone={requisicoes.estourou ? 'danger' : requisicoes.perto ? 'warn' : 'ok'}
          />
        ) : null}

        {!conversas.ilimitado && usadas ? (
          <ProgressBar
            value={conversas.proporcao}
            label={`Conversas em ${mes}: ${usadas.conversations} de ${tetoConversas}`}
            tone={conversas.estourou ? 'danger' : conversas.perto ? 'warn' : 'ok'}
          />
        ) : null}

        {/*
          Este aviso já dizia "O widget parou de aparecer", porque o número era um portão. Não é mais:
          passar do limite não interrompe nada. O tom cai de `danger` para `warn` e o texto passa a
          descrever o que de fato acontece — o gasto acompanha o tráfego e quem decide é você.
        */}
        {algumEstourou ? (
          <Notice tone="warn" title="Você passou do limite que definiu">
            O widget <strong>continua</strong> sendo servido: este número avisa, não bloqueia. Vale
            conferir se o
            volume é tráfego real do cliente — e lembrar que ele inclui requisições que qualquer um pode
            gerar de fora de um navegador, então um salto estranho não é necessariamente do site dele.
          </Notice>
        ) : null}

        {isDemoMode ? (
          <Notice tone="info">
            No modo demo o consumo é sempre zero: os números vêm do widget rodando num site de verdade.
            Os tetos em si são salvos normalmente.
          </Notice>
        ) : null}
      </Section>

      <Section
        title="Limite de alerta — requisições"
        description="O número que corresponde ao que se paga."
        action={<SeloPropagacao versionado={false} />}
      >
        <Notice tone="info" title="Requisição não é conversa">
          A configuração é buscada em <strong>todo carregamento de página</strong>, porque o botão
          flutuante precisa da cor e da imagem antes de qualquer clique. Um site com muitas visitas e
          poucas conversas gasta pelas visitas — e o teto de conversas, sozinho, não segura isso.
        </Notice>

        <Switch
          checked={!requisicoes.ilimitado}
          label="Avisar acima de um número de requisições por mês"
          description={
            <>
              Ligado, o painel <strong>avisa</strong> ao passar do número. O widget continua sendo
              servido: nada é interrompido. Serve para você notar um site com tráfego muito acima do
              contratado — não para conter gasto, que é papel do limite de taxa e do orçamento.
            </>
          }
          onChange={(ligado) => setRequestLimit(ligado ? (tetoRequisicoes ?? 100_000) : null)}
        />

        {tetoRequisicoes !== null ? (
          <Field
            label="Avisar acima de"
            hint="O contador zera na virada do mês, em UTC. Ele conta cada requisição servida e grava o total a cada 30 segundos. Como o endpoint é público, inclui tráfego que não veio do site do cliente."
          >
            <Input
              type="number"
              min={0}
              step={1000}
              value={tetoRequisicoes}
              onChange={(event) => {
                const n = Number(event.target.value);
                setRequestLimit(Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0);
              }}
            />
          </Field>
        ) : null}
      </Section>

      <Section
        title="Limite de alerta — conversas"
        description="Volume de atendimento, não de tráfego."
        action={<SeloPropagacao versionado={false} />}
      >
        <Switch
          checked={!conversas.ilimitado}
          label="Avisar acima de um número de conversas por mês"
          description={
            <>
              Conta cada fluxo iniciado. Abrir e fechar sem interagir não conta. Passar do número{' '}
              <strong>não interrompe nada</strong> — nem conversas em andamento, nem novas.
            </>
          }
          onChange={(ligado) =>
            setConversationLimit(ligado ? (tetoConversas ?? DEFAULT_CONVERSATION_LIMIT) : null)
          }
        />

        {tetoConversas !== null ? (
          <Field
            label="Avisar acima de"
            hint={
              conversationLimit === undefined
                ? `Usando o padrão de ${DEFAULT_CONVERSATION_LIMIT.toLocaleString('pt-BR')}. Digite um número para fixar o seu.`
                : 'O contador zera na virada do mês, em UTC.'
            }
            error={erro}
          >
            <Input
              type="number"
              min={0}
              step={100}
              value={tetoConversas}
              onChange={(event) => {
                const n = Number(event.target.value);
                setConversationLimit(Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0);
              }}
            />
          </Field>
        ) : null}
      </Section>
    </div>
  );
}
